import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { Button, type ButtonProps } from '@vs-shop/ui/button';
import { PriceTag, PriceTagOriginalValue } from '@vs-shop/ui/priceTag';
import { clsxm } from '@vs-shop/utils';

import { FramedMedia, type FramedMediaProps } from '../../internal/framedMedia.tsx';
import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The product card: name, imagery, price, availability and click targets,
 * repeated across a grid, a carousel, a recommendation strip and an article
 * block. Flat named parts a tenant arranges in its own JSX, each painting itself.
 *
 *   <ProductCard variant={ProductCardVariant.BASE}>
 *     <ProductCardHeader>
 *       <Link href={url}>
 *         <ProductCardTitle render={<h3 />}>{name}</ProductCardTitle>
 *       </Link>
 *     </ProductCardHeader>
 *
 *     <Link href={url}>
 *       <ProductCardMedia variant={ProductCardVariant.BASE}>
 *         <ProductCardImage src={src} alt={name} width={250} height={187} />
 *         <ProductCardLabels>{labels}</ProductCardLabels>
 *       </ProductCardMedia>
 *     </Link>
 *
 *     <ProductCardPromo>{coupon}</ProductCardPromo>
 *     <ProductCardDescription variant={ProductCardVariant.BASE}>{copy}</ProductCardDescription>
 *
 *     <ProductCardFooter variant={ProductCardVariant.BASE}>
 *       <ProductCardPriceRow variant={ProductCardVariant.BASE}>…</ProductCardPriceRow>
 *       <ProductCardActions variant={ProductCardVariant.BASE}>…</ProductCardActions>
 *     </ProductCardFooter>
 *   </ProductCard>
 *
 * `variant` is the appearance axis, not a size one: the media box changes
 * height, price and availability swap from a row to a stack, the description
 * disappears, the action row gives way to an inline control. It is required on
 * every part that has one, no recipe declares `defaultVariants`, and the
 * opt-out is a named member — `ProductCardVariant.UNSTYLED`, whose recipe entry
 * is the empty string — so even the empty appearance reads out of the same
 * list. A part that paints the same in every appearance carries no `variant`
 * at all.
 *
 * Which parts appear, and in what order, is tenant JSX. The two the card owns
 * are load-bearing and invisible when missing: the root is the container-query
 * context every `@[...]` step resolves against, and the media box is the
 * positioning context the badges hang in.
 *
 * Server-first: every part is a plain server component; the photo renders
 * through the `@vs-shop/image-link` seam, so hydration is the host's decision.
 */

/**
 * The padded box inside the card's container-query viewport. `justify-between`
 * spreads the composed parts over the cell's full height, which keeps a grid of
 * cards with different name lengths aligned on their footers.
 */
export const productCardVariants = cva('relative flex h-full min-w-0 flex-col justify-between', {
  variants: {
    variant: {
      base: 'px-5 py-6',
      minimalist: 'px-5 py-6',
      // Wider cells than a listing grid, so the padding grows with the box.
      recommendation: 'p-4 @[400px]:p-6',
      unstyled: '',
    },
  },
});

type ProductCardVariantName = NonNullable<VariantProps<typeof productCardVariants>['variant']>;

export type ProductCardVariant = ProductCardVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductCardVariant = {
  BASE: 'base',
  MINIMALIST: 'minimalist',
  RECOMMENDATION: 'recommendation',
  /**
   * The opt-out: a tenant painting the part itself. Every recipe carries it as
   * an empty entry rather than leaving the arm out, so what it paints — nothing
   * — is stated in the list instead of decided by absence from it.
   */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductCardVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductCardVariantProps = { readonly variant: ProductCardVariant };

export type ProductCardProps = ComponentProps<'div'> & ProductCardVariantProps;

/**
 * Root of the section, two elements deep: the outer box is the card's cell and
 * the container-query context every `@[...]` step below resolves against, the
 * inner one is the padded body.
 */
export const ProductCard = ({ className, variant, children, ...props }: ProductCardProps) => (
  <div
    data-slot="product-card"
    data-variant={variant}
    className="bg-background @container z-10 flex h-full w-full flex-col"
    {...props}
  >
    <div
      data-slot="product-card-body"
      className={clsxm(productCardVariants({ variant }), className)}
    >
      {children}
    </div>
  </div>
);

/**
 * The name's box, held at a fixed height so a one-line name and a two-line one
 * put the imagery below them at the same offset.
 */
const productCardHeaderClasses = 'h-16';

export type ProductCardHeaderProps = ComponentProps<'div'>;

/** The name's fixed-height box. No `variant`: it reserves the same two lines throughout. */
export const ProductCardHeader = ({ className, ...props }: ProductCardHeaderProps) => (
  <div
    data-slot="product-card-header"
    className={clsxm(productCardHeaderClasses, className)}
    {...props}
  />
);

/** The product name. Two lines, then it clamps. */
const productCardTitleClasses =
  'font-body line-clamp-2 text-[20px]/[26px] font-medium tracking-tight hover:underline';

export type ProductCardTitleProps = ComponentProps<'div'> & {
  /**
   * The element to paint instead of the default `<div>` — the one place a page
   * decides whether this text is a heading, and at what level. A product grid
   * usually should announce its names as headings, which is why the decision is
   * spelled at the call site (`render={<h3 />}`) rather than frozen here.
   *
   * Declared by what this part reads off the element rather than by a tag, so
   * any element with a `className` qualifies.
   */
  readonly render?: RenderElement;
};

/**
 * The product name. Carries the type scale and the clamp; the element is the
 * page's to choose. `hover:underline` lives here rather than on the link a
 * tenant wraps it in, so the affordance survives whichever anchor is used.
 */
export const ProductCardTitle = ({ className, render, ...props }: ProductCardTitleProps) => (
  <RenderedPart
    dataSlot="product-card-title"
    className={clsxm(productCardTitleClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * The imagery box: the positioning context the badges hang in, and the one
 * place the appearances differ in height.
 */
export const productCardMediaVariants = cva('relative my-2', {
  variants: {
    variant: {
      base: 'h-[187px]',
      minimalist: 'h-[150px]',
      recommendation: 'h-[187px] @[400px]:h-[278px]',
      unstyled: '',
    },
  },
});

export type ProductCardMediaProps = ComponentProps<'div'> & ProductCardVariantProps;

/**
 * The imagery box. `relative` is not decoration: the badges below position
 * themselves against this element.
 */
export const ProductCardMedia = ({ className, variant, ...props }: ProductCardMediaProps) => (
  <div
    data-slot="product-card-media"
    className={clsxm(productCardMediaVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The frame the product image sits in — centred rather than covering, because a
 * product photo is a subject on a background and cropping it loses the subject.
 * Shared with {@link ProductCardImagePlaceholder} so the two occupy the same box.
 */
const productCardImageFrameClasses = 'flex h-full w-full items-center justify-center';

/** The photo itself: contained, never cropped, on the card's own background. */
const productCardImageClasses = 'bg-background h-full w-full object-contain';

/**
 * A product photo. `className` lands on the frame, as
 * {@link ProductCardImagePlaceholder}'s does, so the two stay interchangeable in
 * the same box.
 */
export type ProductCardImageProps = FramedMediaProps;

/**
 * The product's photo, centred in its frame.
 *
 * No `variant`: the photo fills the media box in every appearance — the box is
 * what changes height, not what it holds.
 */
export const ProductCardImage = ({ className, ...props }: ProductCardImageProps) => (
  <FramedMedia
    dataSlot="product-card-image"
    frameClassName={clsxm(productCardImageFrameClasses, className)}
    imageClassName={productCardImageClasses}
    {...props}
  />
);

export type ProductCardImagePlaceholderProps = ComponentProps<'div'>;

/**
 * The same frame with no photo in it — for a product whose imagery is missing
 * before anything is requested. What goes inside is the tenant's; this package
 * ships only the box, so the placeholder centres where the photo would have.
 */
export const ProductCardImagePlaceholder = ({
  className,
  ...props
}: ProductCardImagePlaceholderProps) => (
  <div
    data-slot="product-card-image-placeholder"
    className={clsxm(productCardImageFrameClasses, className)}
    {...props}
  />
);

/** The badge column at the leading top corner — season, novelty, promotion. */
const productCardLabelsClasses = 'absolute top-0 left-0 flex flex-col gap-1';

export type ProductCardLabelsProps = ComponentProps<'div'>;

/** The badge column over the imagery's leading top corner. */
export const ProductCardLabels = ({ className, ...props }: ProductCardLabelsProps) => (
  <div
    data-slot="product-card-labels"
    className={clsxm(productCardLabelsClasses, className)}
    {...props}
  />
);

/** The discount medallion, overhanging the media box's trailing top corner. */
const productCardDiscountBadgeClasses = 'absolute -top-3 -right-3 size-[71px]';

export type ProductCardDiscountBadgeProps = ComponentProps<'div'>;

/**
 * The discount medallion. Deliberately overhangs the media box's corner, so it
 * reads as applied to the card rather than printed on the photo.
 */
export const ProductCardDiscountBadge = ({
  className,
  ...props
}: ProductCardDiscountBadgeProps) => (
  <div
    data-slot="product-card-discount-badge"
    className={clsxm(productCardDiscountBadgeClasses, className)}
    {...props}
  />
);

/** The age-restriction mark, at the trailing bottom corner of the media box. */
const productCardAgeBadgeClasses = 'bg-background absolute right-0 bottom-0 rounded-full';

export type ProductCardAgeBadgeProps = ComponentProps<'div'>;

/**
 * The age-restriction mark. Carries the card's own background rather than
 * relying on the photo behind it, so it stays legible over any imagery.
 */
export const ProductCardAgeBadge = ({ className, ...props }: ProductCardAgeBadgeProps) => (
  <div
    data-slot="product-card-age-badge"
    className={clsxm(productCardAgeBadgeClasses, className)}
    {...props}
  />
);

/**
 * The promotional strip under the imagery. Fixed height in every appearance: a
 * card with no promotion still reserves the row, so its neighbours in a grid
 * keep their price rows level.
 */
const productCardPromoClasses = 'h-10';

export type ProductCardPromoProps = ComponentProps<'div'>;

/** The promotional strip — a coupon, a bundle note, nothing at all. */
export const ProductCardPromo = ({ className, ...props }: ProductCardPromoProps) => (
  <div
    data-slot="product-card-promo"
    className={clsxm(productCardPromoClasses, className)}
    {...props}
  />
);

/**
 * The supporting copy. The compact appearances drop it rather than shrink it —
 * three lines of specification are not worth the height in a carousel cell.
 */
export const productCardDescriptionVariants = cva('h-[60px] text-sm', {
  variants: {
    variant: {
      base: 'mb-4',
      minimalist: 'hidden',
      recommendation: 'hidden',
      unstyled: '',
    },
  },
});

export type ProductCardDescriptionProps = ComponentProps<'div'> & ProductCardVariantProps;

/** The supporting copy under the promo strip. */
export const ProductCardDescription = ({
  className,
  variant,
  ...props
}: ProductCardDescriptionProps) => (
  <div
    data-slot="product-card-description"
    className={clsxm(productCardDescriptionVariants({ variant }), className)}
    {...props}
  />
);

/** The bottom block: price and availability, then whatever acts on the product. */
export const productCardFooterVariants = cva('flex', {
  variants: {
    variant: {
      base: 'flex-col',
      minimalist: 'flex-col',
      // Price beside the control rather than above it — the wider cell has room.
      recommendation: 'grid grid-cols-2 items-end gap-4',
      unstyled: '',
    },
  },
});

export type ProductCardFooterProps = ComponentProps<'div'> & ProductCardVariantProps;

/** The bottom block of the card. */
export const ProductCardFooter = ({ className, variant, ...props }: ProductCardFooterProps) => (
  <div
    data-slot="product-card-footer"
    className={clsxm(productCardFooterVariants({ variant }), className)}
    {...props}
  />
);

/**
 * Price and availability together: a row in the listing appearance, a centred
 * stack in the compact one, reversed in the recommendation one, where
 * availability reads above the price.
 */
export const productCardPriceRowVariants = cva('flex', {
  variants: {
    variant: {
      base: 'h-[42px] flex-row items-start justify-between gap-x-4',
      minimalist: 'h-20 flex-col items-center justify-end',
      recommendation: 'flex-col-reverse items-start gap-1',
      unstyled: '',
    },
  },
});

export type ProductCardPriceRowProps = ComponentProps<'div'> & ProductCardVariantProps;

/** Price and availability together. */
export const ProductCardPriceRow = ({ className, variant, ...props }: ProductCardPriceRowProps) => (
  <div
    data-slot="product-card-price-row"
    className={clsxm(productCardPriceRowVariants({ variant }), className)}
    {...props}
  />
);

/**
 * How the price stack aligns in each appearance. A mapping rather than a cva
 * arm because the alignment is `PriceTag`'s own prop — the primitive owns the
 * classes, and this says only which of them the appearance asks for.
 */
const priceAlignByVariant = {
  base: 'start',
  minimalist: 'center',
  recommendation: 'start',
  // The opt-out asks for nothing, so `PriceTag`'s own default stands. Named
  // here for the same reason the recipes name it: an appearance the map omits
  // is one the reader has to infer.
  unstyled: undefined,
} as const satisfies Record<ProductCardVariant, ComponentProps<typeof PriceTag>['align']>;

export type ProductCardPriceProps = ComponentProps<typeof PriceTag> & ProductCardVariantProps;

/**
 * The price stack — a thin binding over `@vs-shop/ui`'s `PriceTag`. The money
 * formatting, the currency and whether to show a pre-discount price are the
 * tenant's; the card contributes only the alignment each appearance asks for.
 */
export const ProductCardPrice = ({ variant, align, ...props }: ProductCardPriceProps) => (
  <PriceTag align={align ?? priceAlignByVariant[variant]} {...props} />
);

/** How large the struck-through original price reads in each appearance. */
const originalPriceSizeByVariant = {
  base: 'md',
  minimalist: 'sm',
  recommendation: 'md',
  unstyled: undefined,
} as const satisfies Record<
  ProductCardVariant,
  ComponentProps<typeof PriceTagOriginalValue>['size']
>;

export type ProductCardPriceOriginalValueProps = ComponentProps<typeof PriceTagOriginalValue> &
  ProductCardVariantProps;

/** The struck-through pre-discount price, sized for the appearance. */
export const ProductCardPriceOriginalValue = ({
  variant,
  size,
  ...props
}: ProductCardPriceOriginalValueProps) => (
  <PriceTagOriginalValue size={size ?? originalPriceSizeByVariant[variant]} {...props} />
);

/**
 * Availability and the notes beside it. Right-aligned where it sits opposite
 * the price, left-aligned where it sits above it.
 */
export const productCardStockVariants = cva('flex flex-col gap-2', {
  variants: {
    variant: {
      base: 'h-full justify-end text-right',
      minimalist: 'justify-end text-right',
      recommendation: 'text-left',
      unstyled: '',
    },
  },
});

export type ProductCardStockProps = ComponentProps<'div'> & ProductCardVariantProps;

/** The availability column. */
export const ProductCardStock = ({ className, variant, ...props }: ProductCardStockProps) => (
  <div
    data-slot="product-card-stock"
    className={clsxm(productCardStockVariants({ variant }), className)}
    {...props}
  />
);

/**
 * A note above the availability line — free shipping, a delivery promise. The
 * compact appearances have no room for it and drop it rather than clamp it.
 */
export const productCardStockNoteVariants = cva('text-sm/[14px] font-medium', {
  variants: {
    variant: {
      base: '',
      minimalist: 'hidden',
      recommendation: 'hidden',
      unstyled: '',
    },
  },
});

export type ProductCardStockNoteProps = ComponentProps<'span'> & ProductCardVariantProps;

/** A note above the availability line. */
export const ProductCardStockNote = ({
  className,
  variant,
  ...props
}: ProductCardStockNoteProps) => (
  <span
    data-slot="product-card-stock-note"
    className={clsxm(productCardStockNoteVariants({ variant }), className)}
    {...props}
  />
);

/** The availability line itself — the same scale in every appearance. */
const productCardStockStatusClasses = 'text-sm/[14px]';

export type ProductCardStockStatusProps = ComponentProps<'div'>;

/** The availability line. No `variant`: it reads the same size throughout. */
export const ProductCardStockStatus = ({ className, ...props }: ProductCardStockStatusProps) => (
  <div
    data-slot="product-card-stock-status"
    className={clsxm(productCardStockStatusClasses, className)}
    {...props}
  />
);

/**
 * The row of controls at the foot of the card. `mt-4` is the row's own place at
 * the end of the footer rather than a gap the footer hands it, so a card
 * composed without a price row still paints the controls where they belong.
 *
 * The compact appearance hides the row instead of omitting it, so cells stay the
 * same height across a carousel.
 */
export const productCardActionsVariants = cva('mt-4', {
  variants: {
    variant: {
      base: 'flex gap-x-3',
      minimalist: 'hidden',
      recommendation: 'flex gap-x-3',
      unstyled: '',
    },
  },
});

export type ProductCardActionsProps = ComponentProps<'div'> & ProductCardVariantProps;

/** The row of controls at the foot of the card. */
export const ProductCardActions = ({ className, variant, ...props }: ProductCardActionsProps) => (
  <div
    data-slot="product-card-actions"
    className={clsxm(productCardActionsVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The primary control — "detail", "add to cart" — filling the row it shares.
 * It shrinks to that row rather than setting the card's minimum width, and each
 * class carries a different failure:
 *
 * - `wrap-anywhere` — not `break-words`: only `anywhere` also shrinks the
 *   element's min-content size, which is what a flex row measures.
 * - `h-auto min-h-12` — a label that wrapped to two lines is otherwise clipped.
 * - `min-w-0 max-w-full` — the same floor removed for content `wrap-anywhere`
 *   cannot help: an icon or badge with no break opportunity at all.
 *
 * None of them changes a single-line label, so their absence is invisible until
 * a tenant ships a long one.
 */
/**
 * Exported because a card whose CTA is a LINK cannot compose the part. `Button`
 * is a client module, so a server caller handing it `render={<a />}` crosses a
 * client boundary it has no business crossing — the same reason
 * `@vs-shop/ui/buttonVariants` exists beside `@vs-shop/ui/button`. A tenant
 * that needs the anchor pairs this constant with that recipe.
 */
export const productCardCtaClasses =
  'flex h-auto min-h-12 max-w-full min-w-0 flex-1 px-6 wrap-anywhere';

export type ProductCardCtaProps = ButtonProps;

/**
 * The card's primary control. A part rather than a call-site decision because
 * "fill the row you share" is the same answer in every tenant; what differs is
 * whether it opens the product or adds it to a basket, which is a `render`
 * element and a label. Composes `@vs-shop/ui`'s `Button`, which keeps its own
 * `data-slot` and disabled/busy semantics.
 */
export const ProductCardCta = ({ className, ...props }: ProductCardCtaProps) => (
  <Button className={clsxm(productCardCtaClasses, className)} {...props} />
);

/** The secondary controls beside it: compare, favourite. */
const productCardActionIconsClasses = 'flex gap-x-2';

export type ProductCardActionIconsProps = ComponentProps<'div'>;

/** The secondary controls beside the primary one. */
export const ProductCardActionIcons = ({ className, ...props }: ProductCardActionIconsProps) => (
  <div
    data-slot="product-card-action-icons"
    className={clsxm(productCardActionIconsClasses, className)}
    {...props}
  />
);

/**
 * One of those controls: a square target carrying a mark and nothing else.
 *
 * `group` is load-bearing — the mark answers to the pointer being over the
 * TARGET rather than over itself, which is the whole reason the target and the
 * mark are two parts on one axis instead of one control with a class.
 *
 * A card's own action, deliberately not the product panel's tool mark: rung 09f
 * ruled that a card must not import `productPanelTools`, and the two are drawn
 * differently anyway.
 */
export type ProductCardActionMarkProps = ButtonProps;

/** A secondary control at the foot of the card. */
export const ProductCardActionMark = ({ className, ...props }: ProductCardActionMarkProps) => (
  <Button
    size="icon"
    data-slot="product-card-action-mark"
    className={clsxm('group hover:bg-button-secondary', className)}
    {...props}
  />
);

/**
 * The mark inside it, and whether what it stands for is already true — the
 * product is in the comparison, on the list, saved.
 *
 * An appearance rather than a second part, because the two states are one mark
 * drawn two ways: settled it carries the brand colour outright, unsettled it
 * only takes it under a pointer.
 */
export const productCardActionMarkIconVariants = cva('size-4', {
  variants: {
    variant: {
      /** Already true: the mark carries the brand colour. */
      active: 'text-primary-intense',
      /** Not yet: the mark takes the colour under a pointer over the target. */
      idle: 'group-hover:text-primary',
      /** The opt-out: a tenant inking the mark itself. */
      unstyled: '',
    },
  },
});

type ProductCardActionMarkIconVariantName = NonNullable<
  VariantProps<typeof productCardActionMarkIconVariants>['variant']
>;

export type ProductCardActionMarkIconVariant = ProductCardActionMarkIconVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductCardActionMarkIconVariant = {
  /** Already true. */
  ACTIVE: 'active',
  /** Not yet. */
  IDLE: 'idle',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductCardActionMarkIconVariantName>;
